/**
 * 角色与权限矩阵 API（staff-rbac-wechat-login:SC-13~SC-17）。
 *
 * 对齐后端 identity 模块 REST 契约：
 * - GET   /roles            -> RoleDetail[]
 * - POST  /roles            -> RoleDetail
 * - GET   /roles/:id        -> RoleDetail（含权限矩阵）
 * - PATCH /roles/:id        -> RoleDetail
 * - GET   /roles/:id/members -> Paginated<RoleMember>
 *
 * 路径从 api.config.ts 导入，禁止内联；Transport 统一解包 `{success, data}` 信封。
 */
import { ApiPaths } from "./api.config";
import { http } from "./transport";
import type { Paginated } from "./campuses.api";

/** 角色适用范围。 */
export type RoleScope = "ORGANIZATION" | "CAMPUS";

/** 适用范围中文标签映射。 */
export const roleScopeLabel: Record<RoleScope, string> = {
  ORGANIZATION: "全机构角色",
  CAMPUS: "校区角色",
};

/** 角色状态。 */
export type RoleStatus = "ACTIVE" | "DISABLED";

/** 角色状态中文标签映射。 */
export const roleStatusLabel: Record<RoleStatus, string> = {
  ACTIVE: "启用",
  DISABLED: "停用",
};

/** 权限动作枚举（对齐后端权限矩阵 actions；GRANT=手动赠送试课、MAKEUP=补消、DISCOUNT=订单减免）。 */
export type PermissionAction =
  | "VIEW"
  | "CREATE"
  | "EDIT"
  | "DELETE"
  | "EXPORT"
  | "COLLECT"
  | "REVERSE"
  | "GRANT"
  | "MAKEUP"
  | "DISCOUNT";

/** 权限动作中文标签映射。 */
export const permissionActionLabel: Record<string, string> = {
  VIEW: "查看",
  CREATE: "新增",
  EDIT: "编辑",
  DELETE: "删除",
  EXPORT: "导出",
  COLLECT: "收款",
  REVERSE: "冲正",
  GRANT: "赠课",
  MAKEUP: "补消",
  REFUND: "登记退款",
  DISCOUNT: "减免",
};

/** 数据范围枚举（对齐后端 dataScope）。 */
export type DataScope = "ALL_ORG" | "GRANTED_CAMPUS" | "SELF";

/** 数据范围中文标签映射。 */
export const dataScopeLabel: Record<DataScope, string> = {
  ALL_ORG: "全机构",
  GRANTED_CAMPUS: "授权校区",
  SELF: "本人",
};

/** 数据范围选项（供下拉使用）。 */
export const dataScopeOptions: { value: DataScope; label: string }[] = [
  { value: "ALL_ORG", label: "全机构" },
  { value: "GRANTED_CAMPUS", label: "授权校区" },
  { value: "SELF", label: "本人" },
];

/** 权限矩阵项（菜单键 × 动作 × 数据范围）。 */
export interface RolePermission {
  /** 菜单标识（如 ADM-09） */
  menuKey: string;
  /** 菜单名称 */
  menuLabel: string;
  /** 动作集合 */
  actions: string[];
  /** 数据范围 */
  dataScope: DataScope;
}

/** 角色详情（含权限矩阵，对齐后端 RoleResponseDto）。 */
export interface RoleDetail {
  /** 角色 ID */
  id: string;
  /** 角色名 */
  name: string;
  /** 适用范围 */
  scope: RoleScope;
  /** 成员数（后端实时派生） */
  memberCount: number;
  /** 状态 */
  status: RoleStatus;
  /** 更新时间（ISO） */
  updatedAt: string;
  /** 权限矩阵（列表项可能不含，详情含） */
  permissions?: RolePermission[];
}

/** 角色成员（对齐后端 /roles/:id/members 列表项）。 */
export interface RoleMember {
  /** 员工 ID */
  staffId: string;
  /** 员工姓名 */
  staffName: string;
  /** 校区 ID */
  campusId: string;
}

/** 可配置菜单键目录项（对齐后端 GET /roles/menu-keys）。 */
export interface MenuKeyCatalogItem {
  /** 菜单标识（如 ADM-09） */
  menuKey: string;
  /** 菜单名称（同名页面带子功能定位消歧） */
  menuLabel: string;
  /** 该菜单真实存在的可配置动作（矩阵勾选项来源） */
  availableActions: PermissionAction[];
}

/** 新建角色请求体。 */
export interface CreateRole {
  /** 角色名 */
  name: string;
  /** 适用范围 */
  scope: RoleScope;
}

/** 编辑角色请求体（partial）。 */
export interface UpdateRole {
  /** 角色名 */
  name?: string;
  /** 权限矩阵 */
  permissions?: RolePermission[];
  /** 状态 */
  status?: RoleStatus;
}

async function listRoles(): Promise<RoleDetail[]> {
  return http.get<RoleDetail[]>(ApiPaths.roles.list);
}

async function listMenuKeys(): Promise<MenuKeyCatalogItem[]> {
  return http.get<MenuKeyCatalogItem[]>(ApiPaths.roles.menuKeys);
}

async function createRole(dto: CreateRole): Promise<RoleDetail> {
  return http.post<RoleDetail>(ApiPaths.roles.create, dto);
}

async function getRole(id: string): Promise<RoleDetail> {
  return http.get<RoleDetail>(ApiPaths.roles.detail(id));
}

async function updateRole(id: string, dto: UpdateRole): Promise<RoleDetail> {
  return http.patch<RoleDetail>(ApiPaths.roles.update(id), dto);
}

async function listRoleMembers(
  id: string,
  page = 1,
  pageSize = 20,
): Promise<Paginated<RoleMember>> {
  return http.get<Paginated<RoleMember>>(ApiPaths.roles.members(id), {
    page,
    pageSize,
  });
}

/** 角色与权限矩阵 API 唯一对外入口。 */
export const roleApi = {
  listRoles,
  listMenuKeys,
  createRole,
  getRole,
  updateRole,
  listRoleMembers,
};
